/**
 * External dependencies
 */
import { LitElement, html, css, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

/**
 * Internal dependencies
 */
import {
	uapRecipeTemplateLibraryConfig,
	uapRtlCreateFilterEvent,
} from '../../utilities';

/**
 * Styles
 */
import styles from './web-component-style.scss?inline';

/**
 * Defines <uap-rtl-integration-menu> custom element
 */
@customElement( 'uap-rtl-integration-menu' )
class UAPRTL_IntegrationMenu extends LitElement {

	// Define style
	static styles = css`${unsafeCSS( styles )}`;

	// Properties
	@property( { type: Object, attribute: false } ) accessor baseResults = null;

	@property( { type: Object, attribute: false } ) accessor filter = {};

	@property( { type: String, attribute: false } ) accessor activeSlug = '';

	@property( { type: Object, attribute: false } ) accessor isMobile = false;

	@property( { type: Object, attribute: false } ) accessor selectEl = null;

	@property( { type: Object, attribute: false } ) accessor menuItems = [];

	// Library config.
	library = uapRecipeTemplateLibraryConfig;

	/**
	 * Called when the component is updated
	 *
	 * @param {Map} changedProperties - The properties that changed
	 */
	updated( changedProperties ) {
		super.updated( changedProperties );

		// Check if baseResults property has changed
		if ( changedProperties.has( 'baseResults' ) ) {
			// If null set the baseResults to all template IDs.
			if ( this.baseResults === null ) {
				this.baseResults = this.library.templates.map( template => template.id );
			}
			// Set the menu based on the template IDs in baseResults
			this._setMenu();
		}


		// Check if the filter property has changed
		if ( changedProperties.has( 'filter' ) ) {
			const newFilter = this.filter;
			const newType   = newFilter.type;

			// Check if the type is 'integration'
			if ( newType === 'integration' ) {
				this.activeSlug = newFilter.filter.code;
			}

			if ( newType !== 'integration' ) {
				this.activeSlug = '';
			}
		}

		// Detect when isMobile property changes.
		if ( changedProperties.has( 'isMobile' ) ) {
			if ( ! this.mobile ) {
				// Close the Select2 dropdown if it is open
				if ( this.selectEl && jQuery( this.selectEl ).data( 'select2' ).isOpen() ) {
					jQuery( this.selectEl ).select2( 'close' );
				}
			}
			this.updateComplete.then( () => {
				this._handleSelect2();
			} );
		}

		// Reset select2 value when activeSlug is empty
		if ( changedProperties.has( 'activeSlug' ) && this.isMobile ) {
			if ( this.activeSlug === '' && this.selectEl ) {
				jQuery( this.selectEl ).val( '-1' ).trigger( 'change.select2' );
			}
		}

	}

	/**
	 * Trigger an integration click
	 *
	 * @param {String} slug - The integration slug
	 *
	 * @return {void}
	 */
	triggerIntegrationClickBySlug( slug ) {
		const item = this.library.integrations.find( ( integration ) => integration.slug === slug );
		if ( item ) {
			this._handleItemClick( item );
		}
	}

	/**
	 * Set the menu based on the template IDs in baseResults
	 *
	 * @return {void}
	 */
	_setMenu() {

		// First we need to gather all the integration IDs from templates that are in baseResults
		const requirements = this.library.templates.reduce( ( acc, template ) => {
			if ( this.baseResults.includes( template.id ) && Array.isArray( template.requirements ) ) {
				acc.push( ...template.requirements );
			}
			return acc;
		}, [] );

		// Bail if there are no integration IDs
		if ( requirements.length === 0 ) {
			this.menuItems = [];
			return;
		}

		// Remove duplicates
		let integrationIDs = [ ...new Set( requirements ) ];
		// Remove -10 and -20 ( Pro and Elite ) from the uniqueIntegrationIds array
		integrationIDs = integrationIDs.filter( ( id ) => ! [ -10, -20 ].includes( id ) );

		// Generate the menu items
		this.menuItems = this.library.integrations
			// Filter out any integrations that are not in the uniqueIntegrationIds array
			.filter( ( integration ) => {
				return integrationIDs.includes( integration.id );
			} )
			// Update the count property on each integration
			.map( ( integration ) => {
				// count the number of times the id appears in the requirements array
				integration.count = requirements.filter( ( id ) => id === integration.id ).length;
				return integration;
			} );
	}

	/**
	 * Get the menu data
	 *
	 * @return {Array} The menu data
	 */
	_getMenuData() {
		// Check library readiness
		const isReady = this.library.isReady || false;
		if ( ! isReady ) {
			return [];
		}

		// If the menu is not set, set it to all categories minus Pro and Elite
		if ( this.menuItems === null ) {
			this.menuItems = this.library.integrations.filter( ( integration ) => {
				return [ -10, -20 ].includes( integration.id );
			} );
		}

		return this.menuItems;
	}

	/**
	 * Handle select2
	 */
	_handleSelect2() {

		this.selectEl = this.shadowRoot.querySelector( 'select' );

		// Destroy select2 if it's not mobile
		if ( ! this.isMobile ) {
			if ( this.selectEl && ! jQuery( this.selectEl ).data( 'select2' ) ) {
				jQuery( this.selectEl ).select2( 'destroy' );
			}
			return;
		}

		// Initialize select2 if it's mobile
		if ( this.selectEl && ! jQuery( this.selectEl ).data( 'select2' ) ) {

			jQuery( this.selectEl ).select2( {
				dropdownParent: jQuery( '#uap-template-library__menu-wrapper' ),
				templateResult: this._renderSelect2Option.bind( this ),
				templateSelection: this._renderSelect2Option.bind( this )
			} ).on( 'select2:select', e => {
				this._handleSelectChange( e.params.data.id )
			} );

			// Add event listener to close the dropdown when clicking outside
			// Overcomming issues with select2 dropdown not closing due to Lit
			document.addEventListener( 'click', this._handleDocumentClick.bind( this ) );
		}

	}

	/**
	 * Handle select onChange event
	 *
	 * @param {String} code - The selected integration code
	 *
	 * @return {void}
	 */
	_handleSelectChange( code ) {
		if ( code === '-1' ) {
			// Reset to All
			this.dispatchEvent( uapRtlCreateFilterEvent( 'all', 'all', this.library.templates ) );
			return;
		}

		const item = this.library.integrations.find( ( i ) => i.code === code );
		this._handleItemClick( item );
	}

	/**
	 * Handle document click - Close select on click outside menu container
	 *
	 * @param {Event} event - The event
	 */
	_handleDocumentClick( event ) {
		const menuContainer = document.getElementById( 'uap-template-library__menu-wrapper' );
		if ( ! menuContainer.contains( event.target ) ) {
			jQuery( this.selectEl ).select2( 'close' );
		}
	}

	/**
	 * Handle an item click
	 *
	 * @param {Object} item - The clicked item
	 *
	 * @return {void}
	 */
	_handleItemClick( item ) {
		// Filter templates that have the selected integration.
		const results = this.library.templates.filter( template => {
			// Ensure template.requirements is defined and is an array
			return Array.isArray( template.requirements ) && template.requirements.includes( item.id );
		} );

		this.dispatchEvent( uapRtlCreateFilterEvent( 'integration', item, results ) );
	}

	/**
	 * Render select2 option
	 *
	 * @param {Object} option - The option
	 *
	 * @return {Object} The rendered option
	 */
	_renderSelect2Option( option ) {
		const code = option.id;
		if ( ! code || code === '-1' ) {
			return option.text;
		}

		// Get the integraton icon from the library
		const integration = this.library.integrations.find( ( i ) => i.code === code );
		if ( ! integration ) {
			return option.text;
		}

		// Create jQuery icon template.
		const $image = jQuery( '<img/>' ).attr( 'src', integration.icon );
		const $icon  = jQuery( '<span/>' ).addClass( 'uap-rtl-select2-icon' ).append( $image );
		const $text  = jQuery( '<span/>' ).addClass( 'uap-rtl-select2-text' ).text( option.text );
		return jQuery( '<div/>' ).append( $icon ).append( $text ).addClass( 'uap-rtl-select2-icon-option' );
	}

	/**
	 * Render mobile menu - Select Dropdown
	 *
	 * @param {Array} items - The menu items
	 *
	 * @return {TemplateResult} The template result
	 */
	_renderMobileMenu( items ) {
		return html`
		<div class="uap-rtl-select2-wrapper">
			<select>
				<option value="-1">${ escapeHTML( __( 'Filter by integration', 'uncanny-automator' ) ) }</option>
				${ items.map( ( item ) => html`
					<option value="${item.code}" ?selected=${item.code === this.activeSlug}>
						${item.name} ( ${item.count} )
					</option>
				` ) }
			</select>
		</div>
		`;
	}

	/**
	 * Render the menu - Default Desktop menu.
	 *
	 * @param {Array} items - The menu items
	 *
	 * @return {TemplateResult} The template result
	 */
	_renderMenu( items ) {
		return html`
			<div class="filter-menu">
				<div class="filter-menu-heading">
					${ escapeHTML( __( 'Integrations', 'uncanny-automator' ) ) }
				</div>

				<div class="filter-menu-items">
					${
						repeat(
							items,
							( item, index ) => index,
							item => {
								// Create item classes
								const itemClasses = [ 'filter-menu-item' ];

								// Add active class if the item is active
								if ( item.code === this.activeSlug ) {
									itemClasses.push( 'filter-menu-item--active' );
								}

								return html`
									<div 
										class="${ itemClasses.join( ' ' ) }"
										@click=${ event => {
											this._handleItemClick( item );
										} }
									>
										<div class="filter-menu-item-icon">
											<uo-icon integration="${ item.code }"></uo-icon>
										</div>
										<div class="filter-menu-item-name">${ item.name }</div>
										<div class="filter-menu-item-count">${ item.count }</div>
									</div>
								`;
							}
						)
					}
				</div>
			</div>
		`;
	}

	/**
	 * Render the component
	 *
	 * @return {TemplateResult} The rendered template
	 */
	render() {
		const menuData = this._getMenuData();
		return this.isMobile
			? html`${this._renderMobileMenu( menuData )}`
			: html`${this._renderMenu( menuData )}`;
	}
}